<template>
  <div
    class="con-vs-tabs vs-tabs"
    :class="[`vs-tabs-${color}`, `vs-tabs-position-${position}`]"
  >
    <div class="con-ul-tabs">
      <ul
        ref="tabList"
        class="ul-tabs vs-tabs--ul"
        :class="`ul-tabs-${alignment}`"
      >
        <li
          v-for="(tab, index) in tabs"
          :key="tab.tabId"
          ref="tabItems"
          class="vs-tabs--li"
          :class="{ activeChild: activeIndex === index }"
          :style="activeIndex === index ? activeTabStyle : null"
          @mouseover="hover = true"
          @mouseout="hover = false"
        >
          <button
            v-bind="tab.$attrs"
            class="vs-tabs--btn"
            :style="tab.icon ? 'display:flex;align-items:center' : ''"
            type="button"
            @click="activateTab(index)"
          >
            <vs-icon
              v-if="tab.icon"
              class="vs-tabs--btn-icon"
              :icon-pack="tab.iconPack"
              :icon="tab.icon"
              :color="color"
            />
            <span v-if="tab.label">{{ tab.label }}</span>
          </button>
          <button
            v-if="tab.tag"
            class="vs-tabs--btn-tag"
            type="button"
            @click.stop="clickTag(tab)"
          >
            <vs-icon
              :icon-pack="tab.iconPack"
              :icon="tab.tag"
              :color="tagColor"
            />
          </button>
        </li>
        <span class="line-vs-tabs" :style="lineStyle" />
      </ul>
    </div>
    <div class="con-slot-tabs"><slot /></div>
  </div>
</template>

<script>
function getColor(color, opacity = 1) {
  if (!color || opacity === 1) return color;

  if (/^#/.test(color)) {
    const hex = color.replace("#", "");
    const normalized =
      hex.length === 3
        ? hex
            .split("")
            .map((character) => character + character)
            .join("")
        : hex;
    const value = parseInt(normalized, 16);
    if (Number.isNaN(value)) return color;
    return `rgba(${(value >> 16) & 255},${(value >> 8) & 255},${
      value & 255
    },${opacity})`;
  }

  if (/^rgb\(/.test(color)) {
    return color.replace(/^rgb\(/, "rgba(").replace(/\)$/, `,${opacity})`);
  }

  return color;
}

export default {
  name: "VsTabs",
  props: {
    modelValue: {
      type: [Number, String],
      default: undefined,
    },
    value: {
      type: [Number, String],
      default: 0,
    },
    color: {
      type: String,
      default: "primary",
    },
    tagColor: {
      type: String,
      default: "primary",
    },
    alignment: {
      type: String,
      default: "left",
    },
    position: {
      type: String,
      default: "top",
    },
  },
  emits: ["update:modelValue", "input", "click-tag"],
  provide() {
    return {
      vsTabs: this,
    };
  },
  data() {
    return {
      tabs: [],
      activeIndex: 0,
      top: "auto",
      height: 2,
      left: 0,
      width: 0,
      hover: false,
      pulse: false,
      initialized: false,
    };
  },
  computed: {
    selectedValue() {
      return this.modelValue === undefined ? this.value : this.modelValue;
    },
    activeTabStyle() {
      return {
        color: getColor(this.color),
      };
    },
    lineStyle() {
      return {
        top: `${this.top}px`,
        left: `${this.left}px`,
        width: `${this.width}px`,
        height: `${this.height}px`,
        background: `linear-gradient(30deg, ${getColor(
          this.color
        )} 0%, ${getColor(this.color, 0.5)} 100%)`,
        boxShadow: `0px 0px 8px 0px ${getColor(this.color, 0.5)}`,
        transform: `scaleX(${this.pulse ? 1.3 : 1})`,
      };
    },
  },
  watch: {
    selectedValue(index) {
      if (this.initialized) this.activateTab(this.parseIndex(index));
    },
    tabs() {
      this.$nextTick(this.syncActiveTab);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.activeIndex = this.parseIndex(this.selectedValue);
      this.activateTab(this.activeIndex, true);
      this.initialized = true;
    });
  },
  methods: {
    registerTab(tab) {
      if (!this.tabs.includes(tab)) {
        this.tabs.push(tab);
        this.$nextTick(this.syncActiveTab);
      }
    },
    unregisterTab(tab) {
      const index = this.tabs.indexOf(tab);
      if (index === -1) return;
      this.tabs.splice(index, 1);
      if (this.activeIndex >= this.tabs.length) {
        this.activeIndex = Math.max(this.tabs.length - 1, 0);
      }
      this.$nextTick(this.syncActiveTab);
    },
    clickTag(tab) {
      this.$emit("click-tag", tab);
    },
    parseIndex(index) {
      if (!this.tabs.length) return 0;
      const numericIndex = parseInt(index, 10);
      if (Number.isNaN(numericIndex) || numericIndex < 0) return 0;
      if (numericIndex >= this.tabs.length) return this.tabs.length - 1;
      if (this.tabs[numericIndex].$attrs.disabled !== undefined) {
        return this.activeIndex;
      }
      return numericIndex;
    },
    activateTab(index, initialAnimation = false) {
      if (!this.tabs.length) return;
      const parsedIndex = this.parseIndex(index);
      const element = this.$refs.tabItems
        ? this.$refs.tabItems[parsedIndex]
        : null;

      if (this.activeIndex === parsedIndex && !initialAnimation && element) {
        this.pulse = true;
        element.classList.add("isActive");
        setTimeout(() => {
          element.classList.remove("isActive");
          this.pulse = false;
        }, 200);
      }

      this.tabs.forEach((tab, tabIndex) => {
        tab.active = tabIndex === parsedIndex;
      });

      if (this.tabs[this.activeIndex]) {
        this.tabs[this.activeIndex].invert = this.activeIndex <= parsedIndex;
      }
      this.tabs[parsedIndex].invert = this.activeIndex > parsedIndex;
      this.tabs[parsedIndex].vertical =
        this.position === "left" || this.position === "right";
      this.activeIndex = parsedIndex;
      this.$emit("update:modelValue", parsedIndex);
      this.$emit("input", parsedIndex);

      if (element) this.changeLinePosition(element, initialAnimation);
    },
    syncActiveTab() {
      if (!this.tabs.length) return;
      this.activeIndex = this.parseIndex(this.activeIndex);
      this.activateTab(this.activeIndex, true);
    },
    changeLinePosition(element, initialAnimation) {
      if (this.position === "left" || this.position === "right") {
        this.top = element.offsetTop;
        this.height = element.offsetHeight;
        this.width = 2;
        return;
      }

      const update = () => {
        if (!this.$refs.tabList) return;
        this.left = element.offsetLeft;
        this.width = element.offsetWidth;
        this.top =
          element.offsetHeight +
          (element.getBoundingClientRect().top -
            this.$refs.tabList.getBoundingClientRect().top);
      };

      if (initialAnimation) setTimeout(update, 100);
      else update();
    },
  },
};
</script>
